import { useCallback, useMemo } from 'react'
import { Button } from '@/components/catalyst-ui/button'
import { GlobeAltIcon, XMarkIcon, PencilSquareIcon, LinkIcon, ArrowUpRightIcon, AdjustmentsVerticalIcon } from '@heroicons/react/24/solid'
import { DocumentDuplicateIcon, Cog6ToothIcon } from '@heroicons/react/24/outline'
import { Input } from '@/components/catalyst-ui/input'
import { showSimpleSuccess } from '@/lib/toast'
import { PublishStatus } from './PublishStatus'
import { PermissionGuard } from '@/app/router/guards'
import { usePermissions } from '@/hooks/usePermissions'
import { PERMISSIONS } from '@/lib/permissions'

const buttonBaseClass = "!bg-white !shadow-[0px_1px_2px_0px_#0000000D] !border-[#E4E4E7] data-focus:!ring-2 data-focus:!ring-indigo-500 data-focus:!ring-offset-2"

export function LivePublishTab({
  publishInfo,
  subdomainEditor,
  isPublishing,
  publishState,
  onPublish,
  onSuccessDone,
  onNavigateToPage,
  onClose,
  isAiCompanionMode,
}) {
  const { hasPermission } = usePermissions()
  const canPublish = hasPermission(PERMISSIONS.PUBLISH)
  const handleCopyUrl = useCallback(() => {
    navigator.clipboard.writeText(publishInfo.displayUrl)
    showSimpleSuccess('URL copied to clipboard')
  }, [publishInfo.displayUrl])

  const handleViewLiveSite = useCallback(() => {
    if (publishInfo.displayUrl && publishInfo.displayUrl !== `https://your-site.${publishInfo.domainSuffix}`) {
      window.open(publishInfo.displayUrl, '_blank', 'noopener,noreferrer')
    }
  }, [publishInfo.displayUrl, publishInfo.domainSuffix])

  const handleOpenSettings = useCallback(() => {
    const path = publishInfo.isDomainActive ? '/domain'
      : publishInfo.isWordpressActive ? '/wordpress'
        : publishInfo.isSubdomainActive ? '/settings'
          : '/settings'
    onNavigateToPage(path)
    onClose?.()
  }, [publishInfo, onNavigateToPage, onClose])

  const isBusy = publishState === 'publish-animation' || publishState === 'publishing' || isPublishing

  const publishButtonText = useMemo(() => {
    if (publishState === 'publish-animation') return 'Checking for errors...'
    if (isPublishing) return 'Publishing...'
    return publishInfo.isFirstTimePublish ? 'Publish Now' : 'Publish Changes'
  }, [isPublishing, publishInfo.isFirstTimePublish, publishState])

  if (publishState === 'publishing' || publishState === 'success') {
    return (
      <div className="space-y-3 bg-[#FAFAFA] rounded-b-xl" key="publishing-progress">
        <PublishStatus
          isPublishing={isPublishing}
          displayUrl={publishInfo.displayUrl}
          onViewLiveSite={handleViewLiveSite}
          onDone={onSuccessDone}
          onNavigateToPage={onNavigateToPage}
          onClose={onClose}
          publishInfo={publishInfo}
          publishState={publishState}
          isAiCompanionMode={isAiCompanionMode}
        />
      </div>
    )
  }

  return (
    <div className="space-y-3 bg-[#FAFAFA] rounded-b-xl">
      <div className="flex items-center justify-between border-b border-[#E4E4E7] py-3 px-4">
        <span className="text-sm font-medium text-[#000000]">Publish Website</span>
        <Button
          outline
          disabled={!publishInfo.isPublished || isBusy}
          onClick={handleViewLiveSite}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full !border-[#E4E4E7] text-xs font-medium text-[#18181B] disabled:text-[#A1A1AA] disabled:border-[#E4E4E7] disabled:cursor-not-allowed !shadow-[0px_1px_2px_0px_#0000000D] !bg-white data-focus:!ring-2 data-focus:!ring-indigo-500 data-focus:!ring-offset-2"
        >
          View Live Site
          <LinkIcon className="w-4 h-4 !text-black" />
        </Button>
      </div>

      <div className="flex flex-col justify-between text-xs text-[#71717A] px-4 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm font-medium text-[#09090B]">Site status</span>
          <span className="flex items-center gap-1 text-sm font-medium text-[#71717A]">
            <span className={`h-2 w-2 rounded-full ${publishInfo.isPublished ? 'bg-[#22C55E]' : 'bg-[#A1A1AA]'}`} />
            {publishInfo.isPublished ? 'Published' : 'Not Published'}
          </span>
        </div>
        {publishInfo.isPublished && (
          <div className="flex text-sm font-medium items-center justify-between gap-2">
            <span className="text-[#09090B]">Last published</span>
            <span className="text-right text-[#71717A]">{publishInfo.lastPublished || '—'}</span>
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 border border-[#E4E4E7] bg-[#F4F4F5] px-4 py-2 text-sm text-[#71717A]">
        {!publishInfo.isPublished ? (
          <>
            <Button outline disabled className={`${buttonBaseClass} text-[#A1A1AA] cursor-default`}>
              <DocumentDuplicateIcon className="w-4 h-4" />
            </Button>
            <span className="flex-1 truncate text-[#71717A] font-medium">Publish to claim your URL</span>
          </>
        ) : (publishInfo.isDomainActive || publishInfo.isWordpressActive) ? (
          <>
            <Button outline onClick={handleCopyUrl} disabled={isBusy} className={buttonBaseClass}>
              <DocumentDuplicateIcon className="w-4 h-4 !text-[#71717A]" />
            </Button>
            <span className="flex-1 truncate text-[#71717A] font-medium">{publishInfo.displayUrl}</span>
            {publishInfo.isDomainActive && (
              <PermissionGuard permission={PERMISSIONS.DOMAIN_WRITE} fallback={null}>
                <Button outline onClick={() => { onNavigateToPage('/domain'); onClose?.(); }} disabled={isBusy} className={`ml-auto ${buttonBaseClass}`} title="Domain Settings">
                  <Cog6ToothIcon className="w-4 h-4 !text-[#09090B]" />
                </Button>
              </PermissionGuard>
            )}
          </>
        ) : (!publishInfo.projectSubdomain || subdomainEditor.editor) ? (
          <>
            <div className="flex-1 flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <Input
                  ref={subdomainEditor.inputRef}
                  type="text"
                  value={subdomainEditor.value}
                  onChange={subdomainEditor.handleChange}
                  onFocus={subdomainEditor.handleFocus}
                  placeholder="Enter subdomain"
                  className="flex-1 !text-sm"
                  disabled={!canPublish}
                  readOnly={!canPublish}
                />
                <span className="text-[#71717A] whitespace-nowrap">.{publishInfo.domainSuffix}</span>
              </div>
            </div>
            {publishInfo.projectSubdomain && canPublish && (
              <Button outline onClick={subdomainEditor.handleCancel} disabled={isBusy} className={buttonBaseClass}>
                <XMarkIcon className="w-4 h-4 !text-[#09090B]" />
              </Button>
            )}
          </>
        ) : (
          <>
            <Button outline onClick={handleCopyUrl} disabled={isBusy} className={buttonBaseClass}>
              <DocumentDuplicateIcon className="w-4 h-4 !text-[#71717A]" />
            </Button>
            <span className="flex-1 truncate text-[#71717A] font-medium">{publishInfo.displayUrl}</span>
            {publishInfo.canEditSubdomain && !isAiCompanionMode && (
              <PermissionGuard permission={PERMISSIONS.DOMAIN_WRITE} fallback={null}>
                <Button outline onClick={subdomainEditor.handleEdit} disabled={isBusy} className={`ml-auto ${buttonBaseClass}`}>
                  <PencilSquareIcon className="w-4 h-4 !text-[#09090B]" />
                </Button>
              </PermissionGuard>
            )}
          </>
        )}
      </div>

      <div className="px-4 flex items-center justify-start gap-2 mb-4">
        {publishInfo.isPublished && (
          <PermissionGuard permission={PERMISSIONS.SITE_SETTINGS_WRITE} fallback={null}>
            <Button
              outline
              onClick={handleOpenSettings}
              disabled={isBusy}
              className={`${buttonBaseClass} !px-3 !py-1.5`}
              title={publishInfo.isDomainActive ? 'Domain Settings' : publishInfo.isWordpressActive ? 'WordPress Settings' : 'Project Settings'}
            >
              <AdjustmentsVerticalIcon className="w-4 h-4 !text-[#09090B]" />
            </Button>
          </PermissionGuard>
        )}
        <PermissionGuard permission={PERMISSIONS.PUBLISH} fallback={null}>
          <Button
            color="primary"
            disabled={!canPublish || isBusy || (subdomainEditor.editor && subdomainEditor.value !== publishInfo.projectSubdomain && subdomainEditor.validation.status !== 'success' && subdomainEditor.validation.status !== null)}
            className={`${publishInfo.isPublished ? 'flex-1' : 'w-full'} !bg-[#5C33FF] text-white items-center justify-center hover:!bg-[#5C33FF]/90 !rounded-lg !text-sm !font-semibold !py-1.5 data-focus:!ring-2 data-focus:!ring-indigo-500 data-focus:!ring-offset-2 disabled:opacity-50`}
            onClick={onPublish}
          >
            {publishButtonText}
            <ArrowUpRightIcon className="!w-4 !h-4 !text-white !ml-1" />
          </Button>
        </PermissionGuard>
      </div>

      {!(publishInfo.isDomainConnected || publishInfo.isWordpressConnected) && (
        <>
          <PermissionGuard permission={PERMISSIONS.PURCHASE_DOMAIN}>
            <div className="pt-2 bg-[#FAFAFA] border-t border-[#E4E4E7] px-4 -mt-1 mb-4">
              <div className="space-y-1 flex flex-col items-center justify-center gap-1">
                <div className="text-sm font-semibold text-[#71717A]">Ready to look professional?</div>
                <div className="text-xs text-[#A1A1AA] font-medium mb-3">Use your own custom domain or buy a new domain</div>
                <Button outline className={`w-full font-semibold text-[#000000] ${buttonBaseClass}`} disabled={isBusy} onClick={() => { onNavigateToPage('/domain'); onClose?.(); }}>
                  <GlobeAltIcon className="w-4 h-4 !mr-2" />
                  Connect Custom Domain
                </Button>
              </div>
            </div>
          </PermissionGuard>
          {!isAiCompanionMode && (
            <PermissionGuard permission={PERMISSIONS.WORDPRESS_WRITE}>
              <div className="pt-2 pb-4 bg-[#FAFAFA] rounded-b-xl border-t border-[#E4E4E7] px-4">
                <div className="space-y-1 flex flex-col items-center justify-center gap-1">
                  <div className="text-sm font-semibold text-[#71717A]">Prefer WordPress?</div>
                  <div className="text-xs text-[#A1A1AA] font-medium mb-3">Sync your design directly to WordPress.org</div>
                  <Button outline className={`w-full font-semibold text-[#000000] ${buttonBaseClass}`} disabled={isBusy} onClick={() => { onNavigateToPage('/wordpress'); onClose?.(); }}>
                    Connect to WordPress
                  </Button>
                </div>
              </div>
            </PermissionGuard>
          )}
        </>
      )}
    </div>
  )
}

